import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import {
  ProtectedRoute,
  SupplierGuard,
  MemberGuard,
  AdminGuard,
  MonitoringGuard
} from './components/auth';
import ResponsiveLayout from './components/ResponsiveLayout';
import { Login } from './pages/Login';
import LoadingSpinner from './components/shared/Display/LoadingSpinner';

// Lazy load all pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MembersGrid = lazy(() => import('./pages/MembersGrid'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const MyQuotes = lazy(() => import('./pages/MyQuotes'));
const QuoteDetail = lazy(() => import('./pages/QuoteDetail'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const SupplierDetail = lazy(() => import('./pages/SupplierDetail'));
const MemberDetail = lazy(() => import('./pages/MemberDetail'));
const MyOrdersPage = lazy(() => import('./pages/MyOrdersPage/MyOrdersPage'));
const NewQuote = lazy(() => import('./pages/NewQuote'));
const NewMember = lazy(() => import('./pages/NewMember'));
const NewSupplier = lazy(() => import('./pages/NewSupplier'));
const MonitoringDashboard = lazy(() => import('./pages/MonitoringDashboard'));
const NavigationDemo = lazy(() => import('./pages/NavigationDemo'));
const UserList = lazy(() => import('./pages/Admin/Users/UserList'));
const UserForm = lazy(() => import('./pages/Admin/Users/UserForm'));
const ExchangeRatesPage = lazy(() => import('./pages/Admin/ExchangeRatesPage'));
const SupplierQuotesPage = lazy(() => import('./components/supplier/SupplierQuotesPage'));
const SupplierQuoteDetail = lazy(() => import('./components/supplier/SupplierQuoteDetail'));
const SupplierQuoteDetailMirror = lazy(() => import('./pages/supplier/SupplierQuoteDetailMirror'));
const Reports = lazy(() => import('./pages/Reports'));
const ConversionRateReport = lazy(() => import('./pages/Reports/ConversionRateReport'));
const SupplierPerformanceReport = lazy(() => import('./pages/Reports/SupplierPerformanceReport'));
const ActiveOrderStatusReport = lazy(() => import('./pages/Reports/ActiveOrderStatusReport').then(module => ({ default: module.ActiveOrderStatusReport })));
const TimelineReport = lazy(() => import('./pages/TimelineReport'));
const CostingSheet = lazy(() => import('./pages/CostingSheet'));

// Token route wrapper for public supplier access
import { useParams } from 'react-router-dom';
const SupplierQuoteTokenRoute = () => {
  const { token } = useParams<{ token: string }>();
  return <SupplierQuoteDetail token={token} />;
};

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <LoadingSpinner size="large" />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public route - Login page */}
          <Route path="/login" element={<Login />} />
        
        {/* Protected routes - All application pages */}
        <Route path="/" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <Navigate to="/dashboard" replace />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <Dashboard />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />
        
        <Route path="/quotes" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <MyQuotes />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />
        
        <Route path="/quotes/new" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <NewQuote />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />
        
        <Route path="/quotes/:id/costing-sheet" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <CostingSheet />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />

        <Route path="/quotes/:id" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <QuoteDetail />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />
        
        <Route path="/quotes/:id/edit" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <NewQuote />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />
        
        {/* Supplier Portal Routes */}
        <Route path="/supplier/quotes" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <SupplierGuard>
                <SupplierQuotesPage />
              </SupplierGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/supplier/quotes/:id" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <SupplierGuard>
                {/* Always use the updated SupplierQuoteDetail with new layout */}
                <SupplierQuoteDetail />
              </SupplierGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />
        
        {/* Public supplier quote access with token */}
        <Route path="/supplier/quotes/token/:token" element={<SupplierQuoteTokenRoute />} />
        
        <Route path="/orders/:id" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <OrderDetail />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />
        
        <Route path="/members" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <MembersGrid />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/members/new" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <NewMember />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/members/:id" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <MemberDetail />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/suppliers" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <Suppliers />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/suppliers/new" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <NewSupplier />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/suppliers/:id" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <SupplierDetail />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/orders" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <MyOrdersPage />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />
        
        <Route path="/monitoring" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <MonitoringGuard>
                <MonitoringDashboard />
              </MonitoringGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />

        {/* Reports Routes */}
        <Route path="/reports" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <Reports />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />

        <Route path="/reports/conversion-rate" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <ConversionRateReport />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />

        <Route path="/reports/active-order-status" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" />}>
              <ResponsiveLayout>
                <ActiveOrderStatusReport />
              </ResponsiveLayout>
            </MemberGuard>
          </ProtectedRoute>
        } />

        <Route path="/reports/supplier-performance" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <SupplierPerformanceReport />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />

        <Route path="/reports/timeline" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <TimelineReport />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin/users" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <UserList />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/users/new" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <UserForm />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/users/:id" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <UserForm />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/users/:id/edit" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <UserForm />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/exchange-rates" element={
          <ProtectedRoute>
            <ResponsiveLayout>
              <AdminGuard>
                <ExchangeRatesPage />
              </AdminGuard>
            </ResponsiveLayout>
          </ProtectedRoute>
        } />

        <Route path="/navigation-demo" element={
          <ProtectedRoute>
            <NavigationDemo />
          </ProtectedRoute>
        } />

        {/* Supplier routes */}
        <Route path="/supplier/quotes" element={
          <ProtectedRoute>
            <SupplierGuard>
              <ResponsiveLayout>
                <SupplierQuotesPage />
              </ResponsiveLayout>
            </SupplierGuard>
          </ProtectedRoute>
        } />


        {/* Public supplier quote token route */}
        <Route path="/supplier/quote/:token" element={
          <SupplierQuoteTokenRoute />
        } />

        {/* Catch all route - redirect to appropriate page based on user type */}
        <Route path="*" element={
          <ProtectedRoute>
            <MemberGuard fallback={<Navigate to="/supplier/quotes" replace />}>
              <Navigate to="/dashboard" replace />
            </MemberGuard>
          </ProtectedRoute>
        } />
      </Routes>
      </Suspense>
    </AuthProvider>
  );
}

export default App;